import {
  type AriaAttributes,
  type HTMLAttributes,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from 'react'
import cx from 'classnames'
import { Icon, type IconName } from '@/components/Icon'
import { Link } from '@/components/Link'
import { byDesign } from '@/infra/design'
import styles from './Button.module.css'

/**
 * Variants of buttons.
 */
export type ButtonVariant =
  | 'default'
  | 'primary'
  | 'secondary'
  | 'ternary'
  | 'success'
  | 'destructive'
  | 'translucent'
  | 'text'
  | 'text-destructive'

/**
 * Button size options.
 */
export type ButtonSize =
  'small' | 'normal' | 'cta' | 'baseline' | 'wide' | 'wide-slim'

export interface ButtonProps extends AriaAttributes {
  /**
   * The button's label.
   */
  label: string

  /**
   * Button type.
   */
  variant?: ButtonVariant

  /**
   * Button size.
   */
  size?: ButtonSize

  /**
   * Press handler.
   */
  onPress?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>

  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean

  /**
   * Display a loading indicator instead of the text and disables interactions.
   */
  isPending?: boolean

  /**
   * Ref to the underlying button.
   */
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>

  /**
   * Button type.
   */
  type?: 'submit' | 'button' | 'reset'

  /**
   * When the {@link type} is `submit`, this attribute can refer to the
   * form that the button belongs to without being nested within the form.
   */
  form?: string

  /**
   * Optional icon for the left side of the label.
   */
  iconLeft?: IconName

  /**
   * Optional icon for the right side of the label.
   */
  iconRight?: IconName

  /**
   * Sets the right icon flush against the label, as a caret, in the classic
   * design.
   */
  iconRightFlush?: boolean

  /**
   * Icon size in pixels in the classic design, where icons otherwise match the
   * label's font size. The refresh design sizes button icons uniformly.
   */
  iconSize?: number

  /**
   * Whether the button fills the width of its container.
   */
  block?: boolean

  /**
   * Whether to remove the border.
   */
  borderless?: boolean

  /**
   * Whether to use the tinted, borderless treatment of the variant.
   */
  flat?: boolean

  /**
   * Class name to apply.
   */
  className?: string

  /**
   * Title attribute, shown as a native tooltip.
   */
  title?: string

  /**
   * Router link destination.
   */
  to?: string

  /**
   * External link URL.
   */
  href?: string

  /**
   * Link target attribute.
   */
  target?: string

  /**
   * When used with `to`, causes the route to be replaced rather than pushed.
   */
  replace?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * Buttons are used to initialize an action. Button labels express what
 * action will occur when the user interacts with it.
 */
export function Button({
  label,
  variant = 'default',
  size = 'normal',
  isDisabled,
  isPending,
  iconLeft,
  iconRight,
  iconRightFlush,
  iconSize,
  block,
  borderless,
  flat,
  type,
  className,
  ...rest
}: ButtonProps) {
  const classicIconSize = byDesign(iconSize, undefined)

  return (
    <RawButton
      type={type}
      isDisabled={isDisabled}
      isPending={isPending}
      className={cx(
        styles.button,
        styles[variant],
        styles[size],
        Boolean(iconLeft || iconRight) && styles.hasIcon,
        iconRightFlush && styles.iconRightFlush,
        block && styles.block,
        isPending && styles.loading,
        borderless && styles.borderless,
        flat && styles.flat,
        isDisabled && styles.disabled,
        type !== 'submit' && styles.link,
        className,
      )}
      data-slot="button"
      {...rest}
    >
      {iconLeft && (
        <span className={styles.iconLeft}>
          <Icon auto size={classicIconSize} name={iconLeft} />
        </span>
      )}
      {label}
      {iconRight && (
        <span className={styles.iconRight}>
          <Icon auto size={classicIconSize} name={iconRight} />
        </span>
      )}
    </RawButton>
  )
}

/**
 * Raw button props.
 */
export interface RawButtonProps extends Omit<
  HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>,
  'onClick'
> {
  children?: ReactNode

  /**
   * Class name to apply.
   */
  className?: string

  /**
   * Press handler.
   */
  onPress?: MouseEventHandler<HTMLButtonElement | HTMLAnchorElement>

  /**
   * Whether the button is disabled.
   */
  isDisabled?: boolean

  /**
   * Whether the button's action is in progress. Presses are ignored meanwhile.
   */
  isPending?: boolean

  /**
   * Ref to the underlying button.
   */
  ref?: Ref<HTMLButtonElement | HTMLAnchorElement>

  /**
   * Button type.
   */
  type?: 'submit' | 'button' | 'reset'

  /**
   * When the {@link type} is `submit`, this attribute can refer to the
   * form that the button belongs to without being nested within the form.
   */
  form?: string

  /**
   * Router link destination.
   */
  to?: string

  /**
   * External link URL.
   */
  href?: string

  /**
   * Link target attribute.
   */
  target?: string

  /**
   * When used with `to`, causes the route to be replaced rather than pushed.
   */
  replace?: boolean

  /**
   * Test ID attribute.
   */
  testId?: string
}

/**
 * A button component that removes all default styling.
 */
export function RawButton({
  className,
  isDisabled,
  isPending,
  onPress,
  ref,
  type = 'button',
  form,
  to,
  href,
  target,
  replace,
  testId,
  ...rest
}: RawButtonProps) {
  // Prevents the default too, so that a pending submit button does not submit
  // its form again.
  const handlePress: RawButtonProps['onPress'] = isPending
    ? (e) => e.preventDefault()
    : onPress

  if (to || href) {
    return (
      <Link
        to={to}
        href={href}
        target={target}
        replace={replace}
        ref={ref as Ref<HTMLAnchorElement>}
        aria-disabled={isDisabled || undefined}
        aria-busy={isPending || undefined}
        tabIndex={isDisabled ? -1 : undefined}
        data-test={testId}
        className={cx(styles.raw, className)}
        onClick={isDisabled ? undefined : handlePress}
        {...rest}
      />
    )
  }

  return (
    <button
      ref={ref as Ref<HTMLButtonElement>}
      type={type}
      form={form}
      disabled={isDisabled}
      aria-busy={isPending || undefined}
      data-test={testId}
      className={cx(styles.raw, className)}
      onClick={handlePress}
      {...rest}
    />
  )
}
